/**
 * Child-device pairing — client helpers + lockdown gate.
 *
 * Once a tablet is paired (via /admin/child-devices on the parent's device →
 * code → /pair on the tablet), this module is the single source of truth
 * for whether the running shell should hide all admin chrome.
 *
 * Persistence: localStorage. The marker survives reloads, app updates, and
 * service-worker swaps so a curious child can't "log out" to escape.
 *
 * Backend reconciliation: `usePairingStatusPoll` hits /child-device/status
 * every 10s. If the parent unpaired the device from their admin device,
 * we clear the marker and reload — the tablet returns to the normal UI on
 * the next render.
 */
import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

const STORAGE_KEY = "sonspeak_child_device_v1";
const DEVICE_ID_KEY = "sonspeak_device_id_v1";
const POLL_INTERVAL_MS = 10_000;

function rand(n) {
  const a = "abcdefghijklmnopqrstuvwxyz0123456789";
  let s = "";
  for (let i = 0; i < n; i += 1) s += a[Math.floor(Math.random() * a.length)];
  return s;
}

/** Stable per-browser device id. Generated once and cached forever. Used
 * as the `device_id` paired with the parent's account. */
export function ensureDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = `dev-${rand(10)}-${rand(10)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    // Private mode / storage disabled — fall back to a per-session id.
    return `dev-ephemeral-${rand(16)}`;
  }
}

/** Returns the paired marker `{ pairing_id, device_id, label, default_profile_id }`
 * or null if this device is not paired. */
export function getChildDeviceMarker() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isChildDevice() {
  return !!getChildDeviceMarker();
}

export function setChildDeviceMarker(marker) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(marker));
  } catch {
    // best-effort
  }
}

export function clearChildDeviceMarker() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort
  }
}

/** Mint a one-time code on the parent's admin device. */
export async function initiatePairing({ label, default_profile_id } = {}) {
  const r = await api.post("/child-device/pair/initiate", {
    label: label || null,
    default_profile_id: default_profile_id || null,
  });
  return r.data;
}

/** Claim the code on the tablet itself. On success, stash the marker so
 * the next render shows the locked-down child shell. */
export async function claimPairing({ code }) {
  const device_id = ensureDeviceId();
  const r = await api.post("/child-device/pair/claim", {
    code: (code || "").trim().toUpperCase(),
    device_id,
    user_agent: navigator.userAgent.slice(0, 300),
  });
  const pairing = r.data?.pairing;
  if (pairing) {
    setChildDeviceMarker({
      pairing_id: pairing.id,
      device_id: pairing.device_id,
      label: pairing.label,
      default_profile_id: pairing.default_profile_id || null,
    });
  }
  return r.data;
}

export async function listPairedDevices() {
  const r = await api.get("/child-device/list");
  return r.data?.devices || [];
}

export async function unpairDevice(pairingId) {
  await api.post(`/child-device/${pairingId}/unpair`);
}

/**
 * Hook that polls the backend for the device's pairing status. If the
 * parent unpairs from another device, this hook clears the marker and
 * fires `onUnpaired()` so the shell can reload into normal mode.
 *
 * Skips polling entirely when the page is hidden (visibilityState !==
 * "visible") to avoid waking the radio on a tablet locked in a kitchen.
 */
export function usePairingStatusPoll({ enabled, onUnpaired }) {
  const tickRef = useRef(null);
  const onUnpairedRef = useRef(onUnpaired);
  useEffect(() => {
    onUnpairedRef.current = onUnpaired;
  }, [onUnpaired]);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return;
      }
      const marker = getChildDeviceMarker();
      if (!marker) return;
      try {
        const r = await api.get("/child-device/status", {
          params: { device_id: marker.device_id },
        });
        if (cancelled) return;
        if (r.data && r.data.active === false) {
          clearChildDeviceMarker();
          if (onUnpairedRef.current) onUnpairedRef.current();
        }
      } catch {
        // Network blip — try again next interval.
      }
    };

    tick(); // first hit on mount
    tickRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [enabled]);
}
